import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { StarIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { Button, ButtonSize } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { useIdeaIdState } from "components/ideas/hooks/useIdea";

type FollowButtonProps = {
  forType: string;
  forId: string;
  onFollow: () => void;
  onUnfollow: () => void;
  tooltip: string;
  size?: ButtonSize;
};

const FollowButton = ({
  forType,
  forId,
  onFollow,
  onUnfollow,
  tooltip,
  size = "small",
}: FollowButtonProps) => {
  const user = useAppSelector((state) => state.user);
  const followObject = util.userIsFollowingContextObject(user, forType, forId);

  const dispatch = useAppDispatch();
  const updateUser = useCallback(
    (updatedUser: UserMe) => dispatch(actions.user.receiveUser(updatedUser)),
    [dispatch],
  );
  const { t } = useTranslation();

  return (
    <Popup
      on="hover"
      trigger={
        <div>
          {followObject.following ? (
            <Button
              size={size}
              variant="soft"
              color="primary"
              onClick={() => {
                if (!onUnfollow) {
                  return;
                }
                const updatedFollowObject = {
                  ...followObject,
                  following: false,
                  unfollowed: true,
                };
                const oldFollowing = (user?.following || []).filter(
                  (f) => f._id !== forId,
                );
                const newFollowing = [updatedFollowObject, ...oldFollowing];
                if (user) {
                  updateUser({ ...user, following: newFollowing });
                }
                onUnfollow();
              }}
            >
              <Icon icon={StarIcon} />
              {t("users.following.title")}
            </Button>
          ) : (
            <Button
              size={size}
              variant="outline"
              onClick={() => {
                if (!onFollow) {
                  return;
                }
                const updatedFollowObject = {
                  ...followObject,
                  following: true,
                  unfollowed: false,
                };
                const oldFollowing = (user?.following || []).filter(
                  (f) => f._id !== forId,
                );
                const newFollowing = [updatedFollowObject, ...oldFollowing];
                if (user) {
                  updateUser({ ...user, following: newFollowing });
                }
                onFollow();
              }}
            >
              <Icon icon={StarIcon} />
              {t("users.following.follow")}
            </Button>
          )}
        </div>
      }
    >
      {tooltip}
    </Popup>
  );
};

type ChallengeFollowButtonProps = {
  challenge: OpenAPI.Schemas["Challenge"];
  size?: ButtonSize;
};

export const ChallengeFollowButton = ({
  challenge,
  size = "small",
}: ChallengeFollowButtonProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const followChallenge = useCallback(() => {
    api.challenges.follow(
      challenge._id,
      () => {
        toast.success(
          `${t("common:capitalise", { key: "generic.challenge" })} followed`,
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [challenge, t]);

  const unfollowChallenge = useCallback(() => {
    api.challenges.unfollow(
      challenge._id,
      () => {
        toast(
          `${t("common:capitalise", { key: "generic.challenge" })} unfollowed`,
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [challenge, t]);

  if (!user) return null;
  return (
    <FollowButton
      size={size}
      forType="challenge"
      forId={challenge._id}
      onFollow={followChallenge}
      onUnfollow={unfollowChallenge}
      tooltip={t("users.following.unfollowInfo")}
    />
  );
};

export const IdeaFollowButton = ({ size = "small" }: { size?: ButtonSize }) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [ideaId] = useIdeaIdState();

  const followIdea = useCallback(() => {
    if (!ideaId) return;
    api.ideas.follow(
      ideaId,
      () => {
        toast.success(
          `${t("common:capitalise", { key: "generic.idea" })} followed`,
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [ideaId, t]);

  const unfollowIdea = useCallback(() => {
    if (!ideaId) return;
    api.ideas.unfollow(
      ideaId,
      () => {
        toast(`${t("common:capitalise", { key: "generic.idea" })} unfollowed`);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [ideaId, t]);

  if (!user || !ideaId) return null;

  return (
    <FollowButton
      size={size}
      forType="idea"
      forId={ideaId}
      onFollow={followIdea}
      onUnfollow={unfollowIdea}
      tooltip={`Follow ${t("generic.ideaWithArticle")} to receive notifications when somebody likes it or posts a comment. When creating or interacting with ${t("generic.ideaWithArticle")} you will automatically start following it.`}
    />
  );
};

type GroupFollowButtonProps = {
  group: OpenAPI.Schemas["Group"];
  size?: ButtonSize;
};

export const GroupFollowButton = ({
  group,
  size = "small",
}: GroupFollowButtonProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const followGroup = useCallback(() => {
    api.groups.follow(
      group._id,
      () => {
        toast.success(t("groups.followed"));
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [group, t]);

  const unfollowGroup = useCallback(() => {
    api.groups.unfollow(
      group._id,
      () => {
        toast(t("groups.unfollowed"));
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [group, t]);

  if (!user) return null;

  return (
    <FollowButton
      size={size}
      forType="group"
      forId={group._id}
      onFollow={followGroup}
      onUnfollow={unfollowGroup}
      tooltip={t("groups.follow.tooltip")}
    />
  );
};
